import { Maximize2, PanelRight, Square } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AgentMode, useAgentContext } from '@Providers';
import { cn } from '@Utils';

import { Button } from '@Components/ui-v2/button';
import { useIsMobile } from '@CustomHooks';

interface ModeSwitcherProps {
  className?: string;
}

interface ModeOption {
  value: AgentMode;
  icon: typeof PanelRight;
  label: string;
}

const MODES: ModeOption[] = [
  { value: 'sidebar', icon: PanelRight, label: 'Sidebar' },
  { value: 'floating', icon: Square, label: 'Floating window' },
  { value: 'fullscreen', icon: Maximize2, label: 'Fullscreen' },
];

const ModeSwitcher = ({ className }: ModeSwitcherProps) => {
  const { t } = useTranslation();
  const { mode, setMode } = useAgentContext();
  const isMobile = useIsMobile();

  // Mobile is force-pinned to fullscreen by the agent provider's resize listener,
  // so the switcher is meaningless — hide it entirely.
  if (isMobile) return null;

  return (
    <div
      className={cn('inline-flex rounded-lg bg-gray-100/70', className)}
      role="tablist"
      aria-label={t('Agent.AriaDisplayMode')}>
      {MODES.map(({ value, icon: Icon, label }) => {
        const isActive = mode === value;
        return (
          <Button
            key={value}
            type="button"
            variant="ghost"
            size="icon"
            role="tab"
            aria-selected={isActive}
            aria-label={label}
            title={label}
            onClick={() => setMode(value)}
            className={cn(
              'h-auto w-auto rounded-md p-1.5 [&_svg]:size-3.5',
              isActive
                ? 'bg-[#fbfaf9] text-[#7c6cf6] hover:bg-[#fbfaf9] hover:text-[#7c6cf6]'
                : 'text-gray-400 hover:bg-transparent hover:text-gray-700',
            )}>
            <Icon strokeWidth={1.75} />
          </Button>
        );
      })}
    </div>
  );
};

export default ModeSwitcher;
